import * as _ from 'underscore';

import ACTIONS from './actions';
import { getState, getStore } from '../../../state/store';

import * as WorkoutElementService from '../../../api/service/workoutElementService';
import * as WorkoutService from '../../../api/service/workoutService';
import * as ProgramService from '../../../api/service/programService';
import * as TeamService from '../../../api/service/teamService';
import * as UserService from '../../../api/service/userService';
import * as AthleteService from '../../../api/service/athleteService';
import * as TagService from '../../../api/service/tagService';

import * as CalendarState from '../calendar/calendar';
import * as ToastState from '../clipboard/toast';
import * as ProgramTypes from '../calendar/programTypes';

import * as Utils from '../../../lib/designerUtils';
import * as ParamTypes from '../../../lib/paramTypes';
import { isLift } from '../../../lib/exerciseUtils';

export const exportProgramToLibrary = async program => {
    const savedProgram = await ProgramService.exportAsProgram(program);
    addFixedLengthProgramToLibrary(savedProgram);
    ToastState.showSuccessMessage('Exported to Library!');
};

export const loadExercises = () => {
    if (
        getState().library.exerciseRef &&
        getState().library.exerciseRef.length > 0
    )
        return;

    WorkoutElementService.getTH().then(response => {
        let tags = [];
        for (let i = 0; i < response.length; i++) {
            tags = tags.concat(response[i].tags);
            response[i].isLift = isLift(response[i]);
        }
        loadExerciseTags(tags);
        getStore().dispatch({
            type: ACTIONS.EXERCISES_LOAD,
            exercises: response
        });
        loadTrainingEffectTags();
    });
};

export const loadTrainingEffectTags = async () => {
    const response = await TagService.getAllTrainingEffectTags();
    getStore().dispatch({
        type: ACTIONS.EXERCISE_TAGS_LOAD,
        tags: response.data.tags
    });
};

export const loadOrgs = orgs => {
    getStore().dispatch({
        type: ACTIONS.ORGS_LOAD,
        orgs
    });
};

export const loadOrgsHealth = orgs => {
    getStore().dispatch({
        type: ACTIONS.ORGS_HEALTH_LOAD,
        orgs
    });
};

export const filterChange = () => {
    getStore().dispatch({
        type: ACTIONS.FILTER_EXERCISES,
        enableTrainHeroicLibraryFilter: 1
    });
};

export const clearTHExercises = () => {
    getStore().dispatch({
        type: ACTIONS.EXERCISES_TH_CLEAR
    });
    getStore().dispatch({
        type: ACTIONS.FILTER_EXERCISES,
        enableTrainHeroicLibraryFilter: 0
    });
};

export const clearTHSchemes = () => {
    getStore().dispatch({
        type: ACTIONS.SCHEMES_TH_CLEAR
    });
};

const getCalendarPrograms = async groupId => {
    let subscribedCalendarPrograms = await TeamService.getAllSubscribedTeams(
        groupId
    );
    let calendarPrograms = await ProgramService.getCalendarPrograms(groupId);
    return calendarPrograms.concat(subscribedCalendarPrograms);
};

export const load = _.debounce(() => {
    // Get all Teams
    const state = getState();
    const sessionsTreeEmpty = !(
        state.library.librarySessionsTree &&
        state.library.librarySessionsTree.length
    );
    const programsTreeEmpty = !(
        state.library.fixedLengthProgramsTree &&
        state.library.fixedLengthProgramsTree.length
    );
    const teamTreeEmpty = !(
        state.library.teamsTree && state.library.teamsTree.length
    );
    const programType = state.calendar.infoTree.programType;

    loadExercises();

    if (sessionsTreeEmpty) {
        WorkoutService.getAll().then(sessions => {
            loadDrawerSessions(sessions);
        });
    }

    if (programsTreeEmpty) {
        ProgramService.getFixedLengthPrograms().then(response => {
            const programs = response.map(program => {
                return {
                    ...program
                };
            });
            loadDrawerFixedPrograms(response);
        });
    }

    if (!ProgramTypes.isProgram(programType)) {
        const groupId = getState().calendar.infoTree.team.group_id;
        getCalendarPrograms(groupId).then(calendars => {
            loadDrawerCalendarPrograms(calendars);
        });
    }

    if (teamTreeEmpty) {
        TeamService.getAll().then(teams => {
            loadTeams(teams);
        });
    }

    if (ProgramTypes.isCoachAthleteCalendar(programType)) {
        fetchAthletes();
    }

    if (ProgramTypes.isInvitedAthleteCalendar(programType)) {
        fetchInvitedAthletes();
    }
}, 500);

export const fetchAthletes = async () => {
    const athleteTreeEmpty = !(
        getState().library.athletesTree &&
        getState().library.athletesTree.length
    );
    if (!athleteTreeEmpty) {
        return;
    }

    const loggedInUser = await UserService.getUserSimple();
    await AthleteService.getAll(loggedInUser.id).then(athletes => {
        loadAthletes(athletes);
    });
};

export const fetchInvitedAthletes = async () => {
    const invitedAthleteTreeEmpty = !(
        getState().library.invitedAthletesTree &&
        getState().library.invitedAthletesTree.length
    );

    if (!invitedAthleteTreeEmpty) {
        return;
    }

    await AthleteService.getAllInvited().then(invited => {
        loadInvitedAthletes(invited);
    });
};

export const getAthleteIdArray = () => {
    const athletes = getState().library.athletesTree;
    return athletes.map(athlete => athlete.id);
};

function loadAthletes(athletes) {
    getStore().dispatch({
        type: ACTIONS.ATHLETES_LOAD,
        athletes
    });
}

function loadInvitedAthletes(invitedAthletes) {
    getStore().dispatch({
        type: ACTIONS.INVITED_ATHLETES_LOAD,
        invitedAthletes
    });
}

export const fetchTeams = async () => {
    if (getState().library.teamsTree.length === 0) {
        const teams = await TeamService.getAll();
        loadTeams(teams);
    }
};

export function loadTeams(teams) {
    getStore().dispatch({
        type: ACTIONS.TEAMS_LOAD,
        teams
    });
}

function loadDrawerSessions(sessions) {
    sessions.map(e => {
        e.typeElement = 'session';
    });
    CalendarState.loadLibrarySessions(getStore().dispatch, sessions);
}

function loadDrawerFixedPrograms(flPrograms) {
    flPrograms.map(e => {
        e.typeElement = 'program';
    });
    CalendarState.loadFixedPrograms(getStore().dispatch, flPrograms);
}

function loadDrawerCalendarPrograms(calPrograms) {
    const cal = calPrograms || [];
    cal.map(e => {
        e.typeElement = 'calendar';
    });
    CalendarState.loadCalendarPrograms(cal);
}

export const loadExerciseTags = tags => {
    getStore().dispatch({
        type: ACTIONS.EXERCISE_TAGS_LOAD,
        tags
    });
};

export const loadLeaderboardTypes = () => {
    getStore().dispatch({
        type: ACTIONS.LEADERBOARDTYPES_LOAD,
        leaderboardTypes: ParamTypes.redzoneTypes
    });
};

export const loadParamTypes = () => {
    getStore().dispatch({
        type: ACTIONS.PARAMTYPES_LOAD,
        paramTypes: ParamTypes.paramTypes
    });
};

export const loadSubscriptions = subscriptions => {
    getStore().dispatch({
        type: ACTIONS.LOAD_SUBSCRIPTIONS,
        subscriptions
    });
};

export const loadSchemes = async () => {
    let schemes = getState().library.schemeRef;
    if (schemes && schemes.length > 0) return;

    schemes = await WorkoutElementService.getSetExerciseTemplates();

    let tags = [];
    for (let i = 0; i < schemes.length; i++) {
        schemes[i].abr = Utils.calculateAbr(schemes[i]);
        tags = tags.concat(schemes[i].tags);
    }

    loadExerciseTags(tags);

    getStore().dispatch({
        type: ACTIONS.SCHEMES_LOAD,
        schemes
    });
};

export const loadSports = async () => {
    const sports = getState().library.sportsRef;
    if (sports && sports.length > 0) return;

    const sportsRef = await ProgramService.getSports();

    getStore().dispatch({
        type: ACTIONS.SPORTS_LOAD,
        sports: sportsRef
    });
};

let exerciseFilterOptions = {};

export const setExerciseFilterOptions = options => {
    exerciseFilterOptions = options;
};

export const getExerciseFilterOptions = () => exerciseFilterOptions;

export const clearCalendarProgramLibrary = () => {
    getStore().dispatch({
        type: ACTIONS.CALENDAR_PROGRAM_CLEAR
    });
};

export const addSubscription = (id, programId, isAthlete = false) => {
    getStore().dispatch({
        type: ACTIONS.LIBRARY_SUBSCRIPTION_ADD,
        id,
        programId,
        isAthlete
    });
};

export const removeSubscription = (id, programId, isAthlete = false) => {
    getStore().dispatch({
        type: ACTIONS.LIBRARY_SUBSCRIPTION_REMOVE,
        id,
        programId,
        isAthlete
    });
};

export const addFixedLengthProgramToLibrary = program => {
    getStore().dispatch({
        type: ACTIONS.FIXED_PROGRAMS_ADD,
        program
    });
};



// WEBPACK FOOTER //
// ./src/state/reducer/library/library.js